import React, { useState, useEffect, useCallback, Suspense, lazy, startTransition, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  getTelegramUser,
  initTelegramWebApp,
  hapticFeedback,
  openExternal,
} from '../utils/telegram';
import type { TelegramUser, Style } from '../types';
import { apiClient } from '../api/client';

// Lazy load heavy HairstyleStudio component
const HairstyleStudio = lazy(() => import('./HairstyleStudio').then(m => ({ default: m.HairstyleStudio })));
import { HairQuizPage } from './HairQuizPage';
import { AiConsultationPage } from './AiConsultationPage';
import { TrainingPage } from './TrainingPage';
import { PromotionsPage } from './PromotionsPage';
// Lazy load ShopTab
const ShopTab = lazy(() => import('../components/ShopTab').then(m => ({ default: m.ShopTab })));
import { CashbackTab } from '../components/CashbackTab';
import { LazyStyleCard } from '../components/LazyStyleCard';
import { HAIRSTYLE_CONFIGS, getHairstyleConfig } from '../config/hairstyles';
import { BRAND } from '../config/brand';
import type { HairQuizVariant } from '../types';

import { useLiteModeContext } from '../hooks/useLiteMode';
import { debugScrollContainer } from '../utils/perfDebug';

// Premium UI Components
import { PremiumButton } from '../components/ui/PremiumButton';
import { TubelightNavbar } from '../components/ui/tubelight-navbar';
import { SupportFooter } from '../components/SupportFooter';
import { YCLIENTS_BOOKING_URL } from '../constants';

// --- TYPES ---
type View = 'home' | 'hairstyle' | 'quiz' | 'faq' | 'booking' | 'shop' | 'loyalty' | 'training' | 'promotions';

interface ActionCard {
  id: View;
  title: string;
  description: string;
  icon: React.ReactNode;
}

// --- ICONS (Inline SVG) ---
const Icons = {
  Scissors: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="6" cy="6" r="3"></circle>
      <circle cx="6" cy="18" r="3"></circle>
      <line x1="20" y1="4" x2="8.12" y2="15.88"></line>
      <line x1="14.47" y1="14.48" x2="20" y2="20"></line>
      <line x1="8.12" y1="8.12" x2="12" y2="12"></line>
    </svg>
  ),
  Brain: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 1.98-3A2.5 2.5 0 0 1 9.5 2Z"></path>
      <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 14.5 2Z"></path>
    </svg>
  ),
  Message: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
    </svg>
  ),
  Calendar: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
      <line x1="16" y1="2" x2="16" y2="6"></line>
      <line x1="8" y1="2" x2="8" y2="6"></line>
      <line x1="3" y1="10" x2="21" y2="10"></line>
    </svg>
  ),
  Sparkles: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l1.912 5.813a2 2 0 001.275 1.275L21 12l-5.813 1.912a2 2 0 00-1.275 1.275L12 21l-1.912-5.813a2 2 0 00-1.275-1.275L3 12l5.813-1.912a2 2 0 001.275-1.275L12 3z"></path>
    </svg>
  ),
  Star: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1.5">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
    </svg>
  ),
  Check: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
  ),
  GraduationCap: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 10v6M2 10l10-5 10 5-10 5z"></path>
      <path d="M6 12v5c3 3 9 3 12 0v-5"></path>
    </svg>
  ),
  Tag: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
      <line x1="7" y1="7" x2="7.01" y2="7"></line>
    </svg>
  ),
};

// --- DATA ---
const ACTION_CARDS: ActionCard[] = [
  { id: 'hairstyle', title: 'Примерить стрижку', description: 'AI-студия', icon: <Icons.Scissors /> },
  { id: 'quiz', title: 'Подбор стиля', description: '6 вопросов', icon: <Icons.Brain /> },
  { id: 'faq', title: 'AI консультация', description: 'Чат с профи', icon: <Icons.Message /> },
  { id: 'booking', title: 'Записаться', description: 'К мастерам HEADMAN', icon: <Icons.Calendar /> },
  { id: 'promotions', title: 'Акции', description: 'Выгодные предложения', icon: <Icons.Tag /> },
  { id: 'training', title: 'Обучение', description: 'Стать барбером', icon: <Icons.GraduationCap /> },
];


// --- MAIN PAGE ---

// Quick loading spinner for lazy components
const QuickLoader = () => (
  <div className="flex items-center justify-center py-20">
    <div className="quick-spinner" />
  </div>
);

export const MainAppPage: React.FC = () => {
  const [, setUser] = useState<TelegramUser | null>(null);
  const [currentView, setCurrentView] = useState<View>('home');
  const [isNavigating, setIsNavigating] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { isLiteMode } = useLiteModeContext();
  const [quizVariant, setQuizVariant] = useState<HairQuizVariant | null>(null);
  const [preselectedStyleId, setPreselectedStyleId] = useState<string | null>(null);
  const [recommendedStyles, setRecommendedStyles] = useState<Style[]>([]);
  const [stylesLoading, setStylesLoading] = useState(true);
  const [showAllStyles, setShowAllStyles] = useState(false);
  const hasBrandLogo = Boolean(BRAND.logos.horizontal?.trim());

  useEffect(() => {
    initTelegramWebApp();
    setUser(getTelegramUser());

    // Загрузка рекомендуемых стилей
    const loadStyles = async () => {
      setStylesLoading(true);
      try {
        const response = await apiClient.getStyles();
        // Защита: если styles undefined — используем пустой массив
        const rawStyles = response?.styles ?? [];
        console.log('[MainAppPage] Loaded styles count:', rawStyles.length);
        
        const validStyles = rawStyles.filter(s => {
          const config = getHairstyleConfig(s.id);
          return config !== undefined && !config.hiddenInCatalog;
        });
        setRecommendedStyles(validStyles.slice(0, 6));
      } catch (error) {
        console.error('Failed to load styles:', error);
        const fallbackStyles: Style[] = HAIRSTYLE_CONFIGS
          .filter(h => !h.hiddenInCatalog)
          .slice(0, 6)
          .map(h => ({
            id: h.id,
            title: h.title,
            prompt_hint: h.description
          }));
        setRecommendedStyles(fallbackStyles);
      } finally {
        setStylesLoading(false);
      }
    };
    loadStyles();

    // Idle-prefetch HairstyleStudio for instant navigation
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(() => {
        import('./HairstyleStudio');
        import('../components/ShopTab');
      }, { timeout: 3000 });
    } else {
      // Fallback: prefetch after 2 seconds
      setTimeout(() => {
        import('./HairstyleStudio');
        import('../components/ShopTab');
      }, 2000);
    }

    const handleOpenBooking = () => { openExternal(YCLIENTS_BOOKING_URL); };
    const handleOpenShop = () => { setCurrentView('shop'); };

    window.addEventListener('openBooking', handleOpenBooking);
    window.addEventListener('openShop', handleOpenShop);

    return () => {
      window.removeEventListener('openBooking', handleOpenBooking);
      window.removeEventListener('openShop', handleOpenShop);
    };
  }, []);


  const handleViewChange = useCallback((view: View) => {
    if (view === 'booking') {
      hapticFeedback('light');
      openExternal(YCLIENTS_BOOKING_URL);
      return;
    }

    // Сбрасываем скролл при смене вкладки - критично для Telegram WebView
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
    
    // Debug scroll (только с ?perf=1)
    setTimeout(() => debugScrollContainer('.app-scroll-container', view), 100);
    
    // Show instant loading feedback for heavy pages
    if (view === 'hairstyle' || view === 'shop') {
      setIsNavigating(true);
      hapticFeedback('light');
      // Use startTransition for non-urgent state updates
      startTransition(() => {
        setCurrentView(view);
        if (view !== 'hairstyle') {
          setQuizVariant(null);
        }
      });
      // Hide loading after a short delay
      setTimeout(() => setIsNavigating(false), 150);
    } else {
      hapticFeedback('light');
      startTransition(() => {
        setCurrentView(view);
        // Reset quiz variant when navigating away from hairstyle
        setQuizVariant(null);
      });
    }
  }, []);

  const handleQuizVariantSelect = (variant: HairQuizVariant) => {
    setQuizVariant(variant);
    setPreselectedStyleId(null);
    setCurrentView('hairstyle');
    hapticFeedback('medium');
  };

  // Обработчик выбора стиля с главной страницы - переход в студию с выбранным стилем
  const handleStyleSelectFromHome = useCallback((styleId: string) => {
    setPreselectedStyleId(styleId);
    setQuizVariant(null);
    handleViewChange('hairstyle');
    hapticFeedback('medium');
  }, [handleViewChange]);

  // Page transition variants - simplified in lite mode
  const pageVariants = isLiteMode ? {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  } : {
    initial: { opacity: 0, x: 20, scale: 0.98 },
    animate: { opacity: 1, x: 0, scale: 1 },
    exit: { opacity: 0, x: -20, scale: 0.98 },
  };

  const pageTransition = isLiteMode ? {
    duration: 0.15,
  } : {
    type: "spring",
    stiffness: 300,
    damping: 30,
  };

  // Отрисовка контента
  const renderContent = () => {
    switch (currentView) {
      case 'hairstyle':
        return (
          <Suspense fallback={<QuickLoader />}>
            <HairstyleStudio 
              initialQuizVariant={quizVariant} 
              initialStyleId={preselectedStyleId}
              onBack={() => {
                setPreselectedStyleId(null);
                handleViewChange('home');
              }} 
            />
          </Suspense>
        );
      case 'quiz':
        return <HairQuizPage onVariantSelect={handleQuizVariantSelect} onBack={() => handleViewChange('home')} />;
      case 'faq':
        return <AiConsultationPage />;
      case 'booking':
        openExternal(YCLIENTS_BOOKING_URL);
        return null;
      case 'shop':
        return (
          <Suspense fallback={<QuickLoader />}>
            <ShopTab />
          </Suspense>
        );
      case 'loyalty':
        return <CashbackTab />;
      case 'training':
        return <TrainingPage onBack={() => handleViewChange('home')} />;
      case 'promotions':
        return <PromotionsPage onBack={() => handleViewChange('home')} />;
      case 'home':
      default:
        const displayedStyles = showAllStyles ? recommendedStyles : recommendedStyles.slice(0, 4);

        return (
          <div className="space-y-8 pb-32">
            {/* Hero Section with Logo */}
            <motion.div 
              className="flex flex-col items-center pt-6 pb-4"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="mb-4">
                {hasBrandLogo && (
                  <img
                    src={BRAND.logos.horizontal}
                    alt={BRAND.name}
                    className="h-16 w-auto max-w-[260px] object-contain"
                    style={{ filter: 'brightness(0) invert(1) drop-shadow(0 2px 8px rgba(0, 0, 0, 0.4))' }}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                      if (fallback) fallback.style.display = 'flex';
                    }}
                  />
                )}
                <div className={`${hasBrandLogo ? 'hidden' : 'flex'} flex-col items-center gap-1`}>
                  <h1 className="text-[34px] leading-none font-semibold text-white uppercase">
                    HEADMAN
                  </h1>
                  <div className="h-px w-24 bg-white/15" />
                  <p className="text-[11px] font-medium text-white/55 uppercase">
                    Barbershop
                  </p>
                </div>
              </div>
              
              <p 
                className="text-sm text-center max-w-xs mb-6"
                style={{ color: 'rgba(255, 255, 255, 0.6)' }}
              >
                {BRAND.description}
              </p>

              <div className="w-full max-w-[280px]">
                <PremiumButton
                  fullWidth
                  variant="primary"
                  onClick={() => handleViewChange('hairstyle')}
                  className="text-lg"
                >
                  Примерить стрижку
                </PremiumButton>
              </div>
            </motion.div>

            {/* Action Cards Grid - Premium */}
            <motion.div 
              className="space-y-3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              <h2 
                className="text-sm font-medium px-1"
                style={{ 
                  color: 'rgba(255, 255, 255, 0.65)',
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
                }}
              >
                Возможности
              </h2>
            <div className="grid grid-cols-2 gap-3">
              {ACTION_CARDS.map((card, index) => {
                return (
                  <motion.button
                    key={card.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ 
                      delay: 0.5 + index * 0.05, 
                      duration: 0.25,
                      ease: [0.25, 0.1, 0.25, 1]
                    }}
                    whileHover={{ 
                      y: -1,
                      transition: { duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }
                    }}
                    whileTap={{ 
                      scale: 0.96,
                      transition: { duration: 0.1, ease: [0.25, 0.1, 0.25, 1] }
                    }}
                    onClick={() => {
                      handleViewChange(card.id);
                    }}
                    className="relative overflow-hidden p-4 rounded-2xl flex flex-col justify-between h-[120px] text-left group"
                    style={{
                      background: 'linear-gradient(145deg, #0E0E0E 0%, #141414 100%)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      boxShadow: `
                        inset 0 1px 0 rgba(255, 255, 255, 0.18),
                        inset 0 -2px 0 rgba(0, 0, 0, 0.5),
                        0 3px 8px rgba(0, 0, 0, 0.4)
                      `,
                      transition: 'all 0.25s ease-out',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = `
                        inset 0 1px 0 rgba(255, 255, 255, 0.22),
                        inset 0 -2px 0 rgba(0, 0, 0, 0.5),
                        0 5px 12px rgba(0, 0, 0, 0.45)
                      `;
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.25)';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = `
                        inset 0 1px 0 rgba(255, 255, 255, 0.18),
                        inset 0 -2px 0 rgba(0, 0, 0, 0.5),
                        0 3px 8px rgba(0, 0, 0, 0.4)
                      `;
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                    onMouseDown={(e) => {
                      e.currentTarget.style.boxShadow = `
                        inset 0 1px 0 rgba(255, 255, 255, 0.02),
                        inset 0 2px 4px rgba(0, 0, 0, 0.4),
                        0 1px 2px rgba(0, 0, 0, 0.4)
                      `;
                    }}
                    onMouseUp={(e) => {
                      e.currentTarget.style.boxShadow = `
                        inset 0 1px 0 rgba(255, 255, 255, 0.22),
                        inset 0 -2px 0 rgba(0, 0, 0, 0.5),
                        0 5px 12px rgba(0, 0, 0, 0.45)
                      `;
                    }}
                  >
                    {/* Top highlight - VISIBLE light reflection for 3D depth */}
                    <div 
                      className="absolute top-0 left-0 right-0 h-[2px] opacity-80 pointer-events-none"
                      style={{
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent)'
                      }}
                    />

                    {/* Subtle hover light reflection */}
                    <div 
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-250 pointer-events-none"
                      style={{
                        background: 'linear-gradient(180deg, rgba(255,255,255,0.02) 0%, transparent 40%)'
                      }}
                    />

                    {/* Icon - VISIBLE iOS Control Center chip for scissors & calendar, calm container for others */}
                    <div
                      className="relative z-10 w-11 h-11 rounded-xl flex items-center justify-center transition-all duration-200 ease-out"
                      style={{
                        background: (card.id === 'hairstyle' || card.id === 'booking') 
                          ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.18) 0%, rgba(255, 255, 255, 0.10) 100%)' 
                          : 'linear-gradient(135deg, rgba(255, 255, 255, 0.04) 0%, rgba(255, 255, 255, 0.02) 100%)',
                        border: (card.id === 'hairstyle' || card.id === 'booking')
                          ? '1px solid rgba(255, 255, 255, 0.3)'
                          : '1px solid rgba(255, 255, 255, 0.08)',
                        boxShadow: (card.id === 'hairstyle' || card.id === 'booking')
                          ? 'inset 0 1px 0 rgba(255, 255, 255, 0.35), inset 0 -2px 0 rgba(0, 0, 0, 0.25), 0 3px 6px rgba(0, 0, 0, 0.3)'
                          : 'inset 0 1px 0 rgba(255, 255, 255, 0.06), inset 0 -1px 0 rgba(0, 0, 0, 0.2), 0 1px 2px rgba(0, 0, 0, 0.2)',
                        color: (card.id === 'hairstyle' || card.id === 'booking')
                          ? 'rgba(255, 255, 255, 1)'
                          : 'rgba(255, 255, 255, 0.65)',
                        transition: 'all 0.2s ease-out',
                      }}
                      onMouseEnter={(e) => {
                        if (card.id === 'hairstyle' || card.id === 'booking') {
                          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.22) 0%, rgba(255, 255, 255, 0.12) 100%)';
                          e.currentTarget.style.boxShadow = 'inset 0 1px 0 rgba(255, 255, 255, 0.4), inset 0 -2px 0 rgba(0, 0, 0, 0.25), 0 4px 8px rgba(0, 0, 0, 0.35)';
                          e.currentTarget.style.transform = 'scale(1.05)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (card.id === 'hairstyle' || card.id === 'booking') {
                          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.18) 0%, rgba(255, 255, 255, 0.10) 100%)';
                          e.currentTarget.style.boxShadow = 'inset 0 1px 0 rgba(255, 255, 255, 0.35), inset 0 -2px 0 rgba(0, 0, 0, 0.25), 0 3px 6px rgba(0, 0, 0, 0.3)';
                          e.currentTarget.style.transform = 'scale(1)';
                        }
                      }}
                    >
                      {React.cloneElement(card.icon as React.ReactElement, { width: 20, height: 20 })}
                    </div>

                    {/* Text content */}
                    <div className="relative z-10">
                      <h3 
                        className="text-sm font-semibold text-white leading-tight mb-0.5"
                        style={{ textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)' }}
                      >
                        {card.title}
                      </h3>
                      <p 
                        className="text-[10px] leading-tight"
                        style={{ 
                          color: 'rgba(255, 255, 255, 0.5)',
                          textShadow: '0 1px 1px rgba(0, 0, 0, 0.2)'
                        }}
                      >
                        {card.description}
                      </p>
                    </div>
                  </motion.button>
                );
              })}
            </div>
            </motion.div>

            {/* Recommended Styles */}
            <motion.div 
              className="space-y-3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.5 }}
            >
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-bold text-white/60 uppercase tracking-wider px-1">
                  Популярные стили
                </h2>
                {recommendedStyles.length > 4 && (
                  <button
                    onClick={() => setShowAllStyles(!showAllStyles)}
                    className="text-xs text-sheriff-accent hover:text-sheriff-accent-soft transition-colors"
                  >
                    {showAllStyles ? 'Свернуть' : 'Все'}
                  </button>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                {stylesLoading ? (
                  [1, 2, 3, 4].map(i => (
                    <div key={i} className="h-48 bg-sheriff-bg-elevated rounded-2xl animate-pulse border border-sheriff-border" />
                  ))
                ) : (
                  displayedStyles.map((style) => {
                    const config = getHairstyleConfig(style.id);
                    if (!config) return null;
                    return (
                      <LazyStyleCard
                        key={style.id}
                        style={{
                          ...style,
                          title: config.title
                        }}
                        onSelect={() => handleStyleSelectFromHome(style.id)}
                        showImage={true}
                        previewImage={config.previewImage}
                        shortDescription={config.shortDescription}
                        variations={config.variations}
                        length={config.length}
                        vibe={config.vibe}
                        tags={config.tags}
                      />
                    );
                  })
                )}
              </div>
            </motion.div>

            {/* Support Footer - в конце контента, перед навигацией */}
            <div className="mt-8 mb-6">
              <SupportFooter />
            </div>
          </div>
        );
    }
  };

  return (
    <div 
      ref={scrollContainerRef}
      className="app-scroll-container text-white font-sans selection:bg-sheriff-accent selection:text-black"
    >
      {/* Instant loading overlay */}
      {isNavigating && (
        <div className="loading-overlay">
          <div className="quick-spinner" />
          <span className="sr-only">Загрузка...</span>
        </div>
      )}

      {/* Background is now in App.tsx - no local AnimatedBackground needed */}

      <main className="relative z-10 max-w-md mx-auto w-full min-h-0">
        <div className="px-4 pt-safe-top pb-navbar hide-scrollbar" style={{ touchAction: 'pan-y' }}>
          {isLiteMode ? (
            // In lite mode, skip AnimatePresence for better performance
            <div className="py-4">
              {renderContent()}
            </div>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={currentView}
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={pageTransition}
                className="py-4"
                style={{ touchAction: 'pan-y' }}
              >
                {renderContent()}
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </main>

      {/* Tubelight Animated Navbar */}
      <TubelightNavbar 
        value={currentView} 
        onChange={(key) => handleViewChange(key as View)} 
      />
    </div>
  );
};

export default MainAppPage;
